/**
 * The text of the Xpillar Intelligence terms, as a document rather than a notice: the
 * same text an agent accepts in the dialog and can re-read from Settings.
 *
 * It is written to be read by an agent who is accountable for what their
 * listing says, so each clause states plainly what the AI does, what stays
 * the agent's responsibility, and what the platform does with their files.
 */
export const AI_TERMS_VERSION = '2026-09'
export const AI_TERMS_EFFECTIVE = '23 September 2026'

interface Clause {
    heading: string
    paragraphs: string[]
}

export const AI_TERMS: Clause[] = [
    {
        heading: 'What Xpillar Intelligence is',
        paragraphs: [
            'Xpillar Intelligence ("the AI") is an assistant inside Hero that reads material you provide and drafts property listings from it. It is a drafting tool. It does not value property, give legal or financial advice, or verify ownership.',
            'These terms apply each time you use the AI and sit alongside the Xpillar terms of use, your agency agreement and the rules of the Board of Valuers, Appraisers, Estate Agents and Property Managers.',
        ],
    },
    {
        heading: 'Material you provide',
        paragraphs: [
            'Land titles, brochures, spreadsheets, photographs, voice notes and messages you send are transmitted to the platform’s AI provider for the sole purpose of drafting your listing. You confirm you are entitled to share each item for that purpose.',
            'Personal details of owners that appear on a title, such as names and identity numbers, are never written into a listing by the AI. You must not paste such details into the chat yourself.',
            'Your uploads and the resulting drafts are stored against your account so you can return to them. Photographs and video remain in your browser until a listing is created.',
        ],
    },
    {
        heading: 'What the AI produces, and who is responsible for it',
        paragraphs: [
            'Headlines, descriptions, marketing copy, categories and other draft content are generated from the material you provide. They are a starting point, not a finished listing.',
            'You remain the listing agent. You are responsible for the accuracy of every statement in a listing you create, whether you typed it or the AI drafted it, exactly as though you had written it yourself.',
            'The AI can misread a document or mishear a recording, particularly numbers, names and addresses. The review screen shows you what it read and what it could not find so that you can correct it before anything is created.',
        ],
    },
    {
        heading: 'Estimated prices',
        paragraphs: [
            'Where your material does not state a price, the AI may estimate a guide figure from size and location and will label it as an estimate.',
            'An estimate is not a valuation and must not be presented to a client as one. You are asked to confirm or replace the asking price before a listing is created.',
        ],
    },
    {
        heading: 'Acting on your behalf',
        paragraphs: [
            'In Settings you choose what happens when you generate a listing: it is kept as a draft, sent for approval, or published to the site. If you choose to publish, the listing appears publicly without any further confirmation from you and without review by another person.',
            'Where you have asked for owner validation, a generated listing is sent for approval rather than published, and the owner is asked to confirm it.',
            'You may change that choice at any time in Settings. Listings already created are not affected.',
        ],
    },
    {
        heading: 'Tokens and charges',
        paragraphs: [
            'AI work spends tokens from your balance at the rates shown in the platform. Work that fails is not charged. Tokens are not refundable for a draft you decide not to use.',
        ],
    },
    {
        heading: 'Acceptable use',
        paragraphs: [
            'You must not use the AI to create listings for property you are not instructed to market, to produce misleading or discriminatory content, or to process material you have no right to share.',
            'Xpillar may suspend access to the AI where these terms are breached, and may review AI activity on your account for that purpose.',
        ],
    },
    {
        heading: 'Availability and changes',
        paragraphs: [
            'AI features are provided as they are, and may be changed, paused or withdrawn. Where these terms are revised in a way that affects your responsibilities, you will be asked to accept the new version before continuing.',
        ],
    },
]

